"use client";
import React from "react";
import { Box, Typography, Button } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";
import NextLink from "next/link";
const flagImages = [
  IMAGES.FLAG_1,
  IMAGES.FLAG_2,
  IMAGES.FLAG_3,
  IMAGES.FLAG_ARABIC,
  IMAGES.FLAG_4,
  IMAGES.FLAG_5,
  IMAGES.FLAG_6,
  IMAGES.FLAG_8,
  IMAGES.FLAG_9,
  IMAGES.FLAG_10,
  IMAGES.FLAG_11,
];

export default function LanguageSection() {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const langNamesRaw = t("languageSection.languages", { returnObjects: true });
  const langNames = Array.isArray(langNamesRaw) ? langNamesRaw : [];

  const languages = [
    ...langNames.map((name, i) => ({ name, flag: flagImages[i] })),
    { name: t("languageSection.comingSoon"), flag: "", isComingSoon: true },
  ];

  return (
    <Box
      aria-label="Zayn AI recruiter supports 50+ languages in AI interview and video interview software"
      sx={{
        position: "relative",
        overflow: "hidden",
        mt: "2%",
        backgroundImage: `url('${IMAGES.COMPARISON_CHART_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "stretch",
        }}
      >
        {/* Left Side - Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            py: { xs: 6, sm: 8, md: 10, lg: 1 },
            px: { xs: 3, sm: 4, md: 6, lg: 8, xl: 12 },
            textAlign: isArabic ? "right" : "left",
            mt: { xs: "8%", sm: "6%", md: "4%", lg: "4%", xl: "3%" },
            ml: isArabic ? 0 : { xs: "1%", sm: "1%", xl: "2%" },
            mr: isArabic ? { xs: "1%", sm: "1%", xl: "2%" } : 0,
            mb: "5%",
          }}
        >
          <Box sx={{ width: "100%" }}>
            {/* Heading */}
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: {
                  xs: "1.75rem",
                  sm: "2rem",
                  md: "2.75rem",
                  lg: "2.75rem",
                  xl: "3.25rem",
                },
                fontWeight: "bold",
                lineHeight: 1.2,
                color: "#fff",
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              {t("languageSection.heading")}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: {
                  xs: "0.875rem",
                  sm: "0.95rem",
                  md: "1rem",
                  lg: "1rem",
                  xl: "1.2rem",
                },
                color: "#FFFFFF",
                mb: { xs: 5, sm: 5, md: 5, lg: 4 },
                lineHeight: 1.7,
                fontWeight: "400",
                fontFamily: "var(--font-outfit)",
                mt: "2%",
              }}
            >
              {t("languageSection.desc")}
            </Typography>

            {/* CTA Button */}
            <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
              <Button
                component="a"
                variant="contained"
                sx={{
                  backgroundColor: "#9B58FF",
                  color: "#fff",
                  px: 4,
                  py: 1.5,
                  borderRadius: "50px",
                  textTransform: "none",
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.95rem", md: "1.1rem" },
                  fontWeight: 600,
                  mb: { xs: 5, sm: 6, md: 6, lg: 6 },
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#fff",
                    color: "#9B58FF",
                    transform: "scale(1.05) translateY(-2px)",
                    boxShadow: "none",
                  },
                }}
              >
                {t("languageSection.cta")}
              </Button>
            </NextLink>

            {/* Language Grid */}
            <Box
              sx={{
                borderTop: "1px solid rgba(217, 217, 217, 0.29)",
                borderBottom: "1px solid rgba(217, 217, 217, 0.29)",
                overflow: "hidden",
                maxWidth: { xs: "100%", md: 700 },
                mb: { xs: 6, sm: 6, md: 6, lg: 0 },
              }}
            >
              {languages
                .reduce((rows, lang, index) => {
                  if (index % 2 === 0) rows.push([lang]);
                  else rows[rows.length - 1].push(lang);
                  return rows;
                }, [])
                .map((row, rowIndex) => (
                  <Box
                    key={rowIndex}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      borderBottom:
                        rowIndex < 5
                          ? "1px solid rgba(217, 217, 217, 0.29)"
                          : "none",
                      backgroundColor:
                        rowIndex % 2 === 0 ? "#10103B" : "transparent",
                    }}
                  >
                    {row.map((lang, colIndex) => (
                      <Box
                        key={colIndex}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexDirection: isArabic ? "row" : "row",
                          gap: 2.5,
                          py: 2.8,
                          pl: isArabic ? 2 : 3,
                          pr: isArabic ? 3 : 2,
                          justifyContent: "flex-start",
                        }}
                      >
                        {lang.isComingSoon ? (
                          <>
                            <Box
                              sx={{
                                width: 36,
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border:
                                  "1.5px dashed rgba(217, 217, 217, 0.29)",
                                borderRadius: "4px",
                                fontSize: "0.9rem",
                                color: "rgba(255,255,255,0.6)",
                                fontFamily: "var(--font-outfit)",
                                fontWeight: 300,
                              }}
                            >
                              +
                            </Box>
                            <Typography
                              sx={{
                                fontSize: "15px",
                                color: "rgba(255,255,255,0.6)",
                                fontWeight: 400,
                                fontFamily: "var(--font-outfit)",
                              }}
                            >
                              {lang.name}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Image
                              src={lang.flag}
                              alt={lang.name}
                              width={36}
                              height={24}
                              style={{
                                borderRadius: "4px",
                                objectFit: "cover",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "15.5px",
                                color: "#FFFFFF",
                                fontWeight: 400,
                                fontFamily: "var(--font-outfit)",
                              }}
                            >
                              {lang.name}
                            </Typography>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>

        {/* Right Side - Image */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            justifyContent: isArabic ? "flex-end" : "flex-end",
            position: "relative",
            pr: 0,
            mt: "20%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Image
              src={IMAGES.LANGUAGES_RIGHT_IMG}
              alt="Dashboard preview"
              width={900}
              height={600}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: isArabic ? "0 24px 24px 0" : "24px 0 0 24px",
                marginLeft: isArabic ? "0" : "12%",
                marginRight: isArabic ? "12%" : "0",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
